import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import {
  SCRAPER_OUTLINE_CLASS,
  SCRAPER_INSPECTOR_CLASS,
  buildInspectorSuggestions,
  buildElementPath,
  describeElement,
  InspectorBreadcrumbItem,
  InspectorSuggestion,
} from '../../utils/cssSelector';
import {
  formatHtmlSnippet,
  highlightHtmlSnippet,
  getParentHtmlSnippet,
  getTextPreview,
} from '../../utils/htmlFormatter';
import { Button, Badge, Input, Card, Spinner } from '../ui';
import { Copy, RefreshCw } from '../ui/icons';

interface InspectorSelection {
  elementLabel: string;
  textPreview?: string;
  suggestions: InspectorSuggestion[];
  breadcrumb: InspectorBreadcrumbItem[];
  parentHtml?: string | null;
}

type SelectorMatchStatus = 'success' | 'error' | null;

const getSelectorFieldClasses = (status: SelectorMatchStatus, baseClasses: string = '') => {
  if (!status) {
    return baseClasses;
  }
  const statusClasses =
    status === 'success'
      ? 'border-green-500 hover:border-green-600 focus:border-green-600 focus:ring-green-500 text-green-600'
      : 'border-red-500 hover:border-red-600 focus:border-red-600 focus:ring-red-500 text-red-600';
  return `${baseClasses} ${statusClasses}`;
};

const getPreviewDocumentContainer = (iframeDocument: Document) =>
  iframeDocument.head || iframeDocument.body || iframeDocument.documentElement;

export interface ScraperPreviewPanelProps {
  previewHtml?: string | null;
  previewSourceUrl?: string | null;
  previewLoading?: boolean;
  previewSelectorInput: string;
  previewSelectorAllInput: string;
  onPreviewSelectorInputChange: (value: string) => void;
  onPreviewSelectorAllInputChange: (value: string) => void;
  previewSelectorError?: string | null;
  previewSelectorAllError?: string | null;
  onPreviewSelectorErrorChange: (value: string | null) => void;
  onPreviewSelectorAllErrorChange: (value: string | null) => void;
  onPreviewRefresh?: () => void;
  activeFieldName?: string | null;
  singlePlaceholder?: string;
  multiPlaceholder?: string;
  onAssignSuggestion?: (selector: string) => void;
}

const ScraperPreviewPanel: React.FC<ScraperPreviewPanelProps> = ({
  previewHtml,
  previewSourceUrl,
  previewLoading = false,
  previewSelectorInput,
  previewSelectorAllInput,
  onPreviewSelectorInputChange,
  onPreviewSelectorAllInputChange,
  previewSelectorError,
  previewSelectorAllError,
  onPreviewSelectorErrorChange,
  onPreviewSelectorAllErrorChange,
  onPreviewRefresh,
  activeFieldName,
  singlePlaceholder,
  multiPlaceholder,
  onAssignSuggestion,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [inspectorSelection, setInspectorSelection] =
    useState<InspectorSelection | null>(null);
  const [inspectorMessage, setInspectorMessage] = useState<string | null>(null);
  const inspectorHighlightRef = useRef<Element | null>(null);
  const inspectorCleanupRef = useRef<(() => void) | null>(null);
  const inspectorMessageTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [singleMatchCount, setSingleMatchCount] = useState<number | null>(null);
  const [multiMatchCount, setMultiMatchCount] = useState<number | null>(null);

  const parentHtmlPreview = useMemo(() => {
    if (!inspectorSelection?.parentHtml) {
      return null;
    }
    const formatted = formatHtmlSnippet(inspectorSelection.parentHtml);
    return highlightHtmlSnippet(formatted);
  }, [inspectorSelection?.parentHtml]);

  const trimmedSingleSelector = previewSelectorInput.trim();
  const trimmedMultiSelector = previewSelectorAllInput.trim();
  const singleStatus: SelectorMatchStatus =
    previewSelectorError || !trimmedSingleSelector
      ? null
      : singleMatchCount === 0
      ? 'error'
      : singleMatchCount && singleMatchCount > 0
      ? 'success'
      : null;
  const multiStatus: SelectorMatchStatus =
    previewSelectorAllError || !trimmedMultiSelector
      ? null
      : multiMatchCount === 0
      ? 'error'
      : multiMatchCount && multiMatchCount > 0
      ? 'success'
      : null;
  const singleFieldClasses = getSelectorFieldClasses(singleStatus);
  const multiFieldClasses = getSelectorFieldClasses(multiStatus);
  const multiCountBadge =
    trimmedMultiSelector &&
    multiMatchCount !== null &&
    !previewSelectorAllError ? (
      <Badge
        variant={multiMatchCount && multiMatchCount > 0 ? 'success' : 'error'}
        size="sm"
        className="ml-2"
      >
        {multiMatchCount ?? 0}
      </Badge>
    ) : null;

  const showInspectorMessage = useCallback((message: string) => {
    setInspectorMessage(message);
    if (inspectorMessageTimeoutRef.current) {
      clearTimeout(inspectorMessageTimeoutRef.current);
    }
    inspectorMessageTimeoutRef.current = setTimeout(() => {
      setInspectorMessage(null);
    }, 2600);
  }, []);

  const clearInspectorHighlight = useCallback(() => {
    if (inspectorHighlightRef.current) {
      inspectorHighlightRef.current.classList.remove(SCRAPER_INSPECTOR_CLASS);
      inspectorHighlightRef.current = null;
    }
  }, []);

  const highlightInspectorElement = useCallback(
    (element: Element) => {
      clearInspectorHighlight();
      element.classList.add(SCRAPER_INSPECTOR_CLASS);
      inspectorHighlightRef.current = element;
    },
    [clearInspectorHighlight]
  );

  const ensurePreviewStyles = useCallback(() => {
    const iframeDocument = iframeRef.current?.contentDocument;
    if (!iframeDocument) {
      return;
    }

    if (
      previewSourceUrl &&
      !iframeDocument.querySelector('base[data-scraper-base]')
    ) {
      const baseEl = iframeDocument.createElement('base');
      baseEl.setAttribute('href', previewSourceUrl);
      baseEl.setAttribute('data-scraper-base', 'true');
      const container = getPreviewDocumentContainer(iframeDocument);
      if (!container) {
        return;
      }
      container.insertBefore(baseEl, container.firstChild);
    }

    if (!iframeDocument.querySelector('style[data-scraper-style="outline"]')) {
      const container = getPreviewDocumentContainer(iframeDocument);
      if (!container) {
        return;
      }

      const styleEl = iframeDocument.createElement('style');
      styleEl.setAttribute('data-scraper-style', 'outline');
      styleEl.textContent = `
        .${SCRAPER_OUTLINE_CLASS} {
          outline: 2px solid rgba(220, 38, 38, 0.9) !important;
          box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.35) inset !important;
          position: relative;
        }

        .${SCRAPER_OUTLINE_CLASS}[data-scraper-selected="true"]::after {
          content: '';
          position: absolute;
          inset: 0;
          border: 2px dashed rgba(220, 38, 38, 0.7);
          pointer-events: none;
        }

        .${SCRAPER_INSPECTOR_CLASS} {
          outline: 2px solid rgba(37, 99, 235, 0.85) !important;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.35) inset !important;
          position: relative;
        }
      `;
      container.appendChild(styleEl);
    }
  }, [previewSourceUrl]);

  const selectInspectorElement = useCallback(
    (element: Element) => {
      const iframeDocument = iframeRef.current?.contentDocument;
      if (!iframeDocument) {
        return false;
      }
      ensurePreviewStyles();
      highlightInspectorElement(element);
      const suggestions = buildInspectorSuggestions(element, iframeDocument);
      setInspectorSelection({
        elementLabel: describeElement(element),
        textPreview: getTextPreview(element),
        suggestions,
        breadcrumb: buildElementPath(element),
        parentHtml: getParentHtmlSnippet(element),
      });
      return true;
    },
    [ensurePreviewStyles, highlightInspectorElement]
  );

  const clearPreviewHighlights = useCallback(() => {
    const iframeDocument = iframeRef.current?.contentDocument;
    if (!iframeDocument) {
      return;
    }

    iframeDocument
      .querySelectorAll(`.${SCRAPER_OUTLINE_CLASS}`)
      .forEach((element) => {
        element.classList.remove(SCRAPER_OUTLINE_CLASS);
        element.removeAttribute('data-scraper-selected');
      });
  }, []);

  const applyPreviewHighlights = useCallback(() => {
    if (!previewHtml) {
      clearPreviewHighlights();
      if (previewSelectorError) {
        onPreviewSelectorErrorChange(null);
      }
      if (previewSelectorAllError) {
        onPreviewSelectorAllErrorChange(null);
      }
      setSingleMatchCount(null);
      setMultiMatchCount(null);
      return;
    }

    const iframeDocument = iframeRef.current?.contentDocument;
    if (!iframeDocument) {
      setSingleMatchCount(null);
      setMultiMatchCount(null);
      return;
    }

    ensurePreviewStyles();
    clearPreviewHighlights();

    let firstMatch: Element | null = null;
    let singleError: string | null = null;
    let allError: string | null = null;
    let singleMatchResult: number | null = null;
    let multiMatchResult: number | null = null;

    const singleSelector = previewSelectorInput.trim();
    const multiSelector = previewSelectorAllInput.trim();

    if (singleSelector) {
      try {
        const element = iframeDocument.querySelector(singleSelector);
        if (element) {
          element.classList.add(SCRAPER_OUTLINE_CLASS);
          firstMatch = element;
          singleMatchResult = 1;
        } else {
          singleMatchResult = 0;
        }
      } catch (error) {
        singleError = 'Invalid selector';
        singleMatchResult = null;
      }
    }

    if (multiSelector) {
      try {
        const elements = iframeDocument.querySelectorAll(multiSelector);
        multiMatchResult = elements.length;
        elements.forEach((element) => {
          element.classList.add(SCRAPER_OUTLINE_CLASS);
          if (!firstMatch) {
            firstMatch = element;
          }
        });
      } catch (error) {
        allError = 'Invalid selector';
        multiMatchResult = null;
      }
    }

    if (previewSelectorError !== singleError) {
      onPreviewSelectorErrorChange(singleError);
    }
    if (previewSelectorAllError !== allError) {
      onPreviewSelectorAllErrorChange(allError);
    }

    if (firstMatch) {
      firstMatch.setAttribute('data-scraper-selected', 'true');
      try {
        firstMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {
        firstMatch.scrollIntoView({ block: 'center' });
      }
    }
    setSingleMatchCount(singleMatchResult);
    setMultiMatchCount(multiMatchResult);
  }, [
    clearPreviewHighlights,
    ensurePreviewStyles,
    onPreviewSelectorAllErrorChange,
    onPreviewSelectorErrorChange,
    previewHtml,
    previewSelectorAllError,
    previewSelectorAllInput,
    previewSelectorError,
    previewSelectorInput,
  ]);

  const handleCopySuggestion = useCallback(
    async (value: string) => {
      try {
        if (
          typeof navigator !== 'undefined' &&
          navigator.clipboard?.writeText
        ) {
          await navigator.clipboard.writeText(value);
        } else if (typeof document !== 'undefined') {
          const textarea = document.createElement('textarea');
          textarea.value = value;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        } else {
          throw new Error('Clipboard unavailable');
        }
        showInspectorMessage('Copied to clipboard');
      } catch (error) {
        console.error('Failed to copy selector', error);
        showInspectorMessage('Unable to copy selector');
      }
    },
    [showInspectorMessage]
  );

  const handleAssignSuggestion = useCallback(
    (selector: string) => {
      if (onAssignSuggestion) {
        onAssignSuggestion(selector);
      } else {
        showInspectorMessage('Select a field to assign this selector');
      }
    },
    [onAssignSuggestion, showInspectorMessage]
  );

  const handleInspectorClick = useCallback(
    (event: MouseEvent) => {
      const targetNode = event.target as Node | null;
      if (!targetNode || targetNode.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const target = targetNode as Element;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      selectInspectorElement(target);
    },
    [selectInspectorElement]
  );

  const handleBreadcrumbSelect = useCallback(
    (element: Element) => {
      selectInspectorElement(element);
    },
    [selectInspectorElement]
  );

  const attachInspectorListeners = useCallback(() => {
    const iframeDocument = iframeRef.current?.contentDocument;
    if (!iframeDocument) {
      return;
    }

    inspectorCleanupRef.current?.();

    const handleClick = (event: MouseEvent) => {
      handleInspectorClick(event);
    };

    iframeDocument.addEventListener('click', handleClick, true);
    const cleanup = () => {
      iframeDocument.removeEventListener('click', handleClick, true);
      inspectorCleanupRef.current = null;
    };
    inspectorCleanupRef.current = cleanup;
  }, [handleInspectorClick]);

  useEffect(() => {
    applyPreviewHighlights();
  }, [applyPreviewHighlights]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    const handleLoad = () => {
      ensurePreviewStyles();
      attachInspectorListeners();
      clearInspectorHighlight();
      setInspectorSelection(null);
      applyPreviewHighlights();
    };

    iframe.addEventListener('load', handleLoad);
    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [
    applyPreviewHighlights,
    attachInspectorListeners,
    clearInspectorHighlight,
    ensurePreviewStyles,
    previewHtml,
  ]);

  useEffect(() => {
    return () => {
      clearPreviewHighlights();
    };
  }, [clearPreviewHighlights]);

  useEffect(() => {
    if (!previewHtml) {
      setInspectorSelection(null);
      clearInspectorHighlight();
      if (inspectorCleanupRef.current) {
        inspectorCleanupRef.current();
      }
    }
  }, [clearInspectorHighlight, previewHtml]);

  useEffect(() => {
    return () => {
      if (inspectorMessageTimeoutRef.current) {
        clearTimeout(inspectorMessageTimeoutRef.current);
      }
      if (inspectorCleanupRef.current) {
        inspectorCleanupRef.current();
      }
      clearInspectorHighlight();
    };
  }, [clearInspectorHighlight]);

  return (
    <Card className="p-4 h-full flex flex-col border border-gray-300">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-bold text-gray-900">
          Preview inspector
        </h3>
        {previewSourceUrl && onPreviewRefresh && (
          <button
            onClick={onPreviewRefresh}
            disabled={previewLoading}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh preview"
          >
            {previewLoading ? (
              <Spinner size="sm" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
      <p className="text-sm text-gray-600 mt-2">
        {previewHtml
          ? activeFieldName
            ? `Use query selectors to highlight matches while editing ${activeFieldName}.`
            : 'Use query selectors to highlight matches in the loaded preview HTML.'
          : 'Load preview data to inspect the HTML and test query selectors.'}
      </p>

      {previewHtml ? (
        <div className="mt-4 flex flex-col gap-3 flex-1 min-h-0">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Input
                label="Query selector"
                value={previewSelectorInput}
                onChange={(event) => {
                  onPreviewSelectorInputChange((event.target as HTMLInputElement).value);
                  onPreviewSelectorAllInputChange('');
                }}
                placeholder={singlePlaceholder || 'e.g., .property-title'}
                helperText={
                  previewSelectorError || 'Highlights the first matching element'
                }
                error={previewSelectorError || undefined}
                className={singleFieldClasses}
                autoComplete="off"
                fullWidth
              />
            </div>
            <div className="flex-1">
              <div className="relative">
                <Input
                  label="Query selector all"
                  value={previewSelectorAllInput}
                  onChange={(event) => {
                    onPreviewSelectorAllInputChange((event.target as HTMLInputElement).value);
                    onPreviewSelectorInputChange('');
                  }}
                  placeholder={multiPlaceholder || 'e.g., .property-title'}
                  helperText={
                    previewSelectorAllError || 'Highlights all matching elements'
                  }
                  error={previewSelectorAllError || undefined}
                  className={multiFieldClasses}
                  autoComplete="off"
                  fullWidth
                />
                {multiCountBadge && (
                  <div className="absolute right-3 top-8">
                    {multiCountBadge}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border border-gray-300 rounded overflow-hidden flex-1 min-h-0 bg-white flex flex-col">
            <div className="relative flex-1 h-[400px] min-h-[400px]">
              <iframe
                ref={iframeRef}
                title="scraper-preview-html"
                srcDoc={previewHtml || ''}
                style={{
                  border: 'none',
                  width: '100%',
                  height: '400px',
                  backgroundColor: 'white',
                  display: 'block',
                }}
                sandbox="allow-same-origin"
              />
              {previewLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-65 flex items-center justify-center flex-col gap-2">
                  <Spinner size="md" />
                  <p className="text-sm text-gray-600">
                    Updating preview with current selectors...
                  </p>
                </div>
              )}
            </div>
            <div className="border-t border-gray-300 bg-gray-50 px-3 py-2">
              {inspectorSelection ? (
                <div>
                  <nav aria-label="element path" className="flex items-center text-xs">
                    {inspectorSelection.breadcrumb.map((item, index) => {
                      const isLast =
                        index === inspectorSelection.breadcrumb.length - 1;
                      return (
                        <React.Fragment key={`${item.label}-${index}`}>
                          {index > 0 && <span className="mx-1 text-gray-400">›</span>}
                          <button
                            type="button"
                            className={`text-xs hover:underline ${
                              isLast ? 'text-gray-900' : 'text-gray-600'
                            }`}
                            onClick={() => handleBreadcrumbSelect(item.element)}
                          >
                            {item.label}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  </nav>
                  {inspectorSelection.textPreview && (
                    <p className="text-xs text-gray-600 mt-1.5">
                      "{inspectorSelection.textPreview}"
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-600">
                  Click anywhere inside the preview to capture selectors.
                </p>
              )}

              {inspectorSelection?.suggestions.length ? (
                inspectorSelection.suggestions.map((suggestion) => (
                  <div
                    key={suggestion.id}
                    className="grid grid-cols-1 sm:grid-cols-[110px_minmax(0,1fr)_auto] gap-1.5 items-center mt-1.5 min-h-[72px]"
                  >
                    <p className="text-xs text-gray-600 min-w-[96px]">
                      {suggestion.label}
                    </p>
                    <code className="font-mono text-xs break-words border border-gray-300 rounded px-1.5 py-1 bg-white max-h-12 overflow-hidden">
                      {suggestion.value}
                    </code>
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAssignSuggestion(suggestion.value)}
                      >
                        Assign
                      </Button>
                      <button
                        className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-600"
                        onClick={() => handleCopySuggestion(suggestion.value)}
                        title="Copy selector"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {typeof suggestion.matches === 'number' && (
                      <p className="text-xs text-gray-600 col-span-1 sm:col-span-1">
                        {suggestion.matches === 1
                          ? '1 match'
                          : `${suggestion.matches} matches`}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-600 block mt-1">
                  Up to three CSS selectors will be shown here.
                </p>
              )}

              {parentHtmlPreview && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600 block mb-1">
                    Parent element HTML
                  </p>
                  <pre
                    className="font-mono text-xs whitespace-pre break-words border border-gray-300 rounded p-2 bg-white max-h-[200px] overflow-auto [&_.token.tag-name]:text-blue-600 [&_.token.tag-name]:font-semibold [&_.token.attr-name]:text-primary-600 [&_.token.attr-value]:text-green-700 [&_.token.punctuation]:text-gray-600"
                    dangerouslySetInnerHTML={{ __html: parentHtmlPreview }}
                  />
                </div>
              )}

              {inspectorMessage && (
                <p className="text-xs text-green-600 block mt-1.5">
                  {inspectorMessage}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 border border-dashed border-gray-300 rounded min-h-[180px] flex items-center justify-center px-4 text-center bg-gray-50">
          <p className="text-sm text-gray-600">
            Load preview data to inspect the page HTML and test selectors.
          </p>
        </div>
      )}
    </Card>
  );
};

export default ScraperPreviewPanel;
